import type Phaser from 'phaser';

export const DESIGN_WIDTH = 720;
export const DESIGN_HEIGHT = 1280;

export const fontDisplay = 'Fredoka';
/** One chunky family everywhere. The plain body face is not used. */
export const fontBody = fontDisplay;

/** Kept for the few call sites that want one family. */
export const font = fontDisplay;

/**
 * Text rules, in design pixels (the 720-wide board):
 * - One rounded display face, Fredoka, at every size. Weight is 700.
 * - Nothing renders under 24px. At a 390-wide phone that is about 13 CSS px.
 * - On parchment and other light pills, text under 28px is solid dark brown with no outline.
 * - Cream type on a red pill keeps a 2px dark-red outline.
 * - 28px and up keeps the heavier display outline for scenic headings, scores,
 *   and button labels. That shadow is a hard offset, never a blur.
 * - Parchment uses dark brown. Red power pills use cream.
 * - Every text object is rasterized at the device pixel ratio and placed on whole pixels.
 */
export const MIN_TEXT_PX = 24;
export const DISPLAY_MIN_PX = 28;
export const THIN_STROKE_PX = 2;
export const powerStroke = '#7A1E12';

export const textInk = {
  parchment: '#3B2414',
  wood: '#FFF4DC',
} as const;

export const palette = {
  pine: 0x2a1c12,
  pineCss: '#2a1c12',
  felt: 0x22160d,
  feltAlt: 0x2a1c12,
  frame: 0xc4844a,
  frameDeep: 0x5c3418,
  panel: 0x6b3e22,
  panelEdge: 0xe7c98a,
  gold: 0xf0c14a,
  goldCss: '#f0c14a',
  ink: '#fff6e8',
  inkDim: '#f3e2c2',
  moss: '#8fbf7a',
  danger: '#e07a6a',
  wood: 0x8a5a32,
  cream: 0xfff4dc,
  creamCss: '#FFF4DC',
  bark: 0x5c3418,
  sky: 0xe7b07a,
  well: 0x22160d,
  brown: '#3B2414',
  brownDeep: '#3B2414',
};

export type TextSurface = 'parchment' | 'wood';

export function textResolution(): number {
  if (typeof window === 'undefined') return 1;
  const ratio = window.devicePixelRatio || 1;
  return ratio > 0 ? ratio : 1;
}

function pxOf(size: string): number {
  const parsed = Number.parseInt(size, 10);
  return Number.isFinite(parsed) ? parsed : MIN_TEXT_PX;
}

export function surfaceColor(surface: TextSurface): string {
  return surface === 'wood' ? textInk.wood : textInk.parchment;
}

function storyFace(
  size: string,
  color: string,
): Phaser.Types.GameObjects.Text.TextStyle {
  const px = Math.max(MIN_TEXT_PX, pxOf(size));
  const onRed = px < DISPLAY_MIN_PX && color.toLowerCase() === textInk.wood.toLowerCase();
  return {
    fontFamily: fontDisplay,
    fontSize: `${px}px`,
    fontStyle: '700',
    color,
    stroke: onRed ? powerStroke : undefined,
    strokeThickness: onRed ? THIN_STROKE_PX : 0,
    padding: onRed ? { x: 3, y: 3 } : undefined,
    resolution: textResolution(),
  };
}

/** Fredoka for scenic headings, scores, and button labels. Outline at 28px and up. */
export function displayStyle(
  size: string,
  color = '#fff8ee',
): Phaser.Types.GameObjects.Text.TextStyle {
  const px = Math.max(DISPLAY_MIN_PX, pxOf(size));
  const stroke = Math.min(5, Math.max(3, Math.round(px / 10)));
  return {
    fontFamily: fontDisplay,
    fontSize: `${px}px`,
    fontStyle: '700',
    color,
    stroke: textInk.parchment,
    strokeThickness: stroke,
    padding: { x: 4, y: 4 },
    shadow: {
      offsetX: 0,
      offsetY: 2,
      color: textInk.parchment,
      blur: 0,
      fill: true,
      stroke: false,
    },
    resolution: textResolution(),
  };
}

/** Fredoka on a solid panel. Light surfaces under 28px have no outline. */
export function headingStyle(
  size: string,
  surface: TextSurface = 'parchment',
): Phaser.Types.GameObjects.Text.TextStyle {
  return storyFace(size, surfaceColor(surface));
}

/** Dark Fredoka for parchment, ribbons, and other cream surfaces. */
export function inkStyle(size: string): Phaser.Types.GameObjects.Text.TextStyle {
  return headingStyle(size, 'parchment');
}

/** Fredoka for species, captions, and other labels. Light surfaces have no outline. */
export function bodyStyle(
  size: string,
  color: string = textInk.parchment,
): Phaser.Types.GameObjects.Text.TextStyle {
  return storyFace(size, color);
}

/** Body copy colored for the surface it sits on. */
export function panelStyle(
  size: string,
  surface: TextSurface = 'parchment',
): Phaser.Types.GameObjects.Text.TextStyle {
  return bodyStyle(size, surfaceColor(surface));
}

export function formatScore(value: number): string {
  return value.toLocaleString('en-US');
}
